/**
 * The canonical locale list, copied verbatim from `marketing/src/i18n/config.ts` so the two
 * halves of the product can never disagree about what "de" or "en-US" means (POS2-1133).
 *
 * Not to be confused with `backend/app/services/ai/language.py`, whose eleven codes are a
 * *content*-language detector for supplier catalogues and menus. That list describes the
 * language of customer data; this one describes the language of the interface.
 */
export const locales = ["en", "de", "fr", "es", "nl", "no", "sv", "zh", "ar", "en-US"] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "en";

/**
 * The subset that actually ships a catalogue and is therefore offered in the picker. `locales`
 * is the vocabulary; this is the inventory. Adding French means translating it and adding it
 * here — never one without the other, or the picker offers a locale that renders as English.
 */
export const appLocales: readonly Locale[] = ["en", "de"];

export function isAppLocale(value: string): value is Locale {
  return (appLocales as readonly string[]).includes(value);
}

/** Endonyms, not English names: a German user scans the picker for "Deutsch", not "German". */
export const localeNames: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  nl: "Nederlands",
  no: "Norsk",
  sv: "Svenska",
  zh: "中文",
  ar: "العربية",
  "en-US": "English (US)",
};

export const rtlLocales: Locale[] = ["ar"];

export function getDirection(locale: string): "ltr" | "rtl" {
  return rtlLocales.includes(locale as Locale) ? "rtl" : "ltr";
}
